import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/catalog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const EMPTY_FORM = { name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "" };

export const CheckoutDialog = () => {
  const { checkoutOpen, setCheckoutOpen, items, total, clearCart } = useCart();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!/^[0-9]{10}$/.test(form.phone.replace(/\s/g, ""))) return "Please enter a valid 10-digit phone number";
    if (!form.address.trim()) return "Please enter your delivery address";
    if (!form.city.trim() || !form.state.trim()) return "Please enter city and state";
    if (!/^[0-9]{6}$/.test(form.pincode)) return "Please enter a valid 6-digit pincode";
    return null;
  };

  const verifyPayment = async (orderNumber, resp) => {
    const { data } = await axios.post(`${API}/orders/verify`, {
      order_number: orderNumber,
      razorpay_order_id: resp.razorpay_order_id,
      razorpay_payment_id: resp.razorpay_payment_id,
      razorpay_signature: resp.razorpay_signature,
    });
    return data;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    if (items.length === 0) return toast.error("Your cart is empty");

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({ product_id: i.slug, qty: i.qty })),
      });

      if (data.payment_configured && data.razorpay_order_id) {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Could not load payment gateway");

        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Eleven Stars",
          description: `Order ${data.order_number}`,
          order_id: data.razorpay_order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#1A4331" },
          handler: async (resp) => {
            try {
              await verifyPayment(data.order_number, resp);
              setResult({ orderNumber: data.order_number, paid: true });
              clearCart();
            } catch {
              toast.error("Payment verification failed. Contact support with your order number.");
            }
          },
          modal: {
            ondismiss: () => {
              toast.info("Payment not completed. Your order is saved — you can pay again anytime.");
            },
          },
        });
        rzp.open();
      } else {
        setResult({ orderNumber: data.order_number, paid: false });
        clearCart();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setCheckoutOpen(false);
    if (result) {
      setResult(null);
      setForm(EMPTY_FORM);
    }
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={close}>
      <DialogContent data-testid="checkout-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F9F6F0]">
        {result ? (
          <div data-testid="order-success" className="py-10 px-4 text-center">
            <CheckCircle2 className="w-16 h-16 text-[#16a34a] mx-auto mb-4" />
            <h2 className="font-display text-3xl font-semibold text-[#1c2826] mb-2">
              {result.paid ? "Payment Successful!" : "Order Reserved!"}
            </h2>
            <p className="text-[#4B5563] mb-1">
              Order number: <span data-testid="order-number" className="font-bold text-[#1A4331]">{result.orderNumber}</span>
            </p>
            <p className="text-sm text-[#4B5563] max-w-md mx-auto">
              {result.paid
                ? "Thank you for shopping with Eleven Stars. We will ship your order shortly and share tracking details on your phone."
                : "Online payment is being enabled on our store. Your order is saved — our team will contact you on your phone to complete payment and delivery."}
            </p>
            <Button
              data-testid="continue-shopping"
              onClick={close}
              className="mt-6 rounded-full bg-[#1A4331] hover:bg-[#122e22] text-white px-8"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#1A4331]">Checkout</DialogTitle>
            </DialogHeader>
            <div className="grid md:grid-cols-5 gap-6">
              <div className="md:col-span-3 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="co-name">Full Name *</Label>
                    <Input data-testid="checkout-name" id="co-name" value={form.name} onChange={set("name")} className="bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="co-phone">Phone *</Label>
                    <Input data-testid="checkout-phone" id="co-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="co-email">Email</Label>
                    <Input data-testid="checkout-email" id="co-email" type="email" value={form.email} onChange={set("email")} className="bg-white" />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="co-address">Delivery Address *</Label>
                    <Textarea data-testid="checkout-address" id="co-address" value={form.address} onChange={set("address")} className="bg-white" rows={2} />
                  </div>
                  <div>
                    <Label htmlFor="co-city">City *</Label>
                    <Input data-testid="checkout-city" id="co-city" value={form.city} onChange={set("city")} className="bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="co-state">State *</Label>
                    <Input data-testid="checkout-state" id="co-state" value={form.state} onChange={set("state")} className="bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="co-pincode">Pincode *</Label>
                    <Input data-testid="checkout-pincode" id="co-pincode" value={form.pincode} onChange={set("pincode")} className="bg-white" />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="bg-white rounded-xl border border-[#E5E0D8] p-4 space-y-3">
                  <h3 className="font-semibold text-[#1c2826] text-sm uppercase tracking-wider">Order Summary</h3>
                  {items.map((i) => (
                    <div key={i.slug} className="flex justify-between text-sm">
                      <span className="text-[#4B5563]">
                        {i.name} × {i.qty}
                      </span>
                      <span className="font-medium text-[#1c2826]">{formatINR(i.price * i.qty)}</span>
                    </div>
                  ))}
                  <div className="border-t border-[#E5E0D8] pt-3 flex justify-between">
                    <span className="font-semibold text-[#1c2826]">Total</span>
                    <span data-testid="checkout-total" className="font-bold text-lg text-[#1A4331]">
                      {formatINR(total)}
                    </span>
                  </div>
                  <Button
                    data-testid="pay-now-button"
                    onClick={handleSubmit}
                    disabled={submitting || items.length === 0}
                    className="w-full rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white py-6 font-semibold"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                      </>
                    ) : (
                      <>Pay {formatINR(total)}</>
                    )}
                  </Button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-[#4B5563]">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#16a34a]" />
                    Secure payment by Razorpay · UPI, Cards, Netbanking
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
